import { useGetPracticeGroups } from '@/react-query/insights/usePracticeGroups';

import { useGetFilteredClinicalData } from '../data/getClinicalData';

import { calculateRates } from './calculateRates';
import { usePracticeCounts } from './practiceRates';
import type { TBenchmarkingCounts } from './types';

/*
Get counts per practice and then aggregate to the region level.
And calculate rates for the region.
 */
export const useRegionRates = ({
  practices,
}: {
  practices?: (number | string)[];
}) => {
  const { data, isLoading } = useGetFilteredClinicalData({});
  const practiceCounts = usePracticeCounts(data)?.filter((practice) =>
    practices
      ? practice?.practiceId && practices.includes(practice?.practiceId)
      : true,
  );

  const practiceGroups = useGetPracticeGroups();

  const initRegionCounts: (TBenchmarkingCounts & {
    regionId: string;
    regionName?: string;
  })[] = [];

  const regionCounts = practiceCounts?.reduce((acc, practice) => {
    // Get regions current practice belongs to
    const regions = practiceGroups?.data?.data
      ?.filter((group) =>
        group.practices.some((p) => p.practiceVideaId === practice.practiceId),
      )
      ?.map((group) => {
        return {
          regionId: group.id,
          regionName: group.name,
        };
      });

    // Add practices counts to each region this practice belongs to
    for (const r of regions ?? []) {
      const region = acc.find(
        (regionCount) => regionCount.regionId === r.regionId,
      );

      if (region) {
        region.completedProduction += practice.completedProduction;
        region.recommendations.planned += practice.recommendations.planned;
        region.recommendations.scheduled += practice.recommendations.scheduled;
        region.recommendations.completed += practice.recommendations.completed;
        region.recommendations.total += practice.recommendations.total;
        region.recommendations.unplanned += practice.recommendations.unplanned;
        region.previousCompletedProduction +=
          practice.previousCompletedProduction;
        region.previousRecommendations.planned +=
          practice.previousRecommendations.planned;
        region.previousRecommendations.scheduled +=
          practice.previousRecommendations.scheduled;
        region.previousRecommendations.completed +=
          practice.previousRecommendations.completed;
        region.previousRecommendations.total +=
          practice.previousRecommendations.total;
        region.previousRecommendations.unplanned +=
          practice.previousRecommendations.unplanned;
        region.recommendations.viewedInVidea +=
          practice.recommendations.viewedInVidea;
        region.previousRecommendations.viewedInVidea +=
          practice.previousRecommendations.viewedInVidea;
      } else {
        acc.push({
          regionId: r.regionId,
          regionName: r.regionName,
          completedProduction: practice.completedProduction,
          recommendations: practice.recommendations,
          previousCompletedProduction: practice.previousCompletedProduction,
          previousRecommendations: practice.previousRecommendations,
        });
      }
    }
    return acc;
  }, initRegionCounts);

  const regionRates = regionCounts?.map((region) => {
    return {
      regionName: region.regionName,
      regionId: region.regionId,
      ...calculateRates(region),
    };
  });

  return { regionRates, isLoading };
};
